<template>
  <div v-if="changeList.length" class="summary my-2">
    <div class="font-bold">Saving these changes will:</div>
    <div v-for="(change, i) in changeList" :key="`${i}-${change}`">
      • {{ change }}
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { getObjDiff, getProperty } from "@/lib/helpers.js";

import CONSTANTS from "@/Constants.js";
import { formatTime } from "@/mixins";
const { APPOINTMENT_STATUS_MAP } = CONSTANTS;

// Determines which keys display a message and the
// order of change messages
const supportedKeys = [
  "status",
  "service_category_id",
  "remaining_minutes",
  "duration_minutes",
  "estimated_end_time",
  "user_id",
  "scheduled_time",
  "accepted_time",
  "dispatch_time",
  "arrival_time",
  "departure_time",
  "preWork",
  "travel",
  "work",
  "duration",
  "crew_members",
  "skip_notifications",
  "assignee"
];

export default {
  props: {
    original: {
      type: Object,
      default: () => ({})
    },
    changes: {
      type: Object,
      default: () => ({})
    },
    estimatedEndTime: {
      type: String,
      default: ""
    }
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    ...mapState(["technicians", "serviceCategories"]),
    changeList() {
      // All keys MUST be added to supportedKeys array to show up in change list!
      const changeList = [];

      // Change list order should match form field order
      const sortedKeys = supportedKeys.filter((field) =>
        Object.prototype.hasOwnProperty.call(this.changes, field)
      );
      sortedKeys.forEach((changedKey) => {
        // look through original for matching key
        // get old value and create string
        if (Object.prototype.hasOwnProperty.call(this.original, changedKey)) {
          const changeText = this.getChangeText(changedKey);
          if (changeText) {
            if (Array.isArray(changeText)) {
              changeText.forEach((change) => changeList.push(change));
            } else {
              changeList.push(changeText);
            }
          }
        }
      });
      return changeList;
    },
    fieldNameMap() {
      return {
        arrival_time: "Arrival Time",
        accepted_time: "Accepted Time",
        dispatch_time: "Travel Started Time",
        departure_time: "Departure Time",
        preWork: "Pre-Work Time",
        travel: "Travel Time",
        work: "Work Time",
        duration: "Duration Time",
        crew_members: "Crew Members",
        skip_notifications: "Do not notify customer"
      };
    }
  },
  methods: {
    getServiceCategoryName(id) {
      if (!id) {
        return "--";
      }
      const serviceCategory = this.serviceCategories.find((sc) => sc.id === id);
      return getProperty(serviceCategory, "name");
    },
    getTechnicianName(id) {
      if (!id) {
        return "Auto Assign";
      }
      const tech = this.technicians.find(
        (tech) => tech.id === id || tech.user_id === id
      );
      return this.fullName(tech);
    },
    getChangeText(changedKey, newValue, oldValue) {
      let formatFn = null;
      switch (changedKey) {
        case "service_category_id": {
          formatFn = this.formatServiceCategory;
          break;
        }
        case "user_id": {
          formatFn = this.formatTechnician;
          break;
        }
        case "duration_minutes": {
          formatFn = this.formatDurationChange;
          break;
        }
        case "remaining_minutes": {
          formatFn = this.formatRemainingDurationChange;
          break;
        }
        case "scheduled_time":
        case "arrival_window_start":
        case "arrival_window_end": {
          formatFn = this.formatScheduledTime;
          break;
        }
        case "status": {
          formatFn = this.formatStatus;
          break;
        }
        case "estimated_end_time": {
          formatFn = this.formatEstimatedEndTime;
          break;
        }
        case "arrival_time":
        case "dispatch_time":
        case "accepted_time":
        case "departure_time":
        case "preWork":
        case "travel":
        case "work":
        case "duration": {
          formatFn = this.formatTimeChanges;
          break;
        }
        case "crew_members": {
          formatFn = this.formatCrewMembers;
          break;
        }
        case "skip_notifications": {
          formatFn = this.formatSkipNotification;
          break;
        }
        case "assignee": {
          formatFn = this.formatChangeAssignee;
          break;
        }
        default: {
          formatFn = this.generateDefaultMessage;
        }
      }
      return formatFn
        ? formatFn(
            newValue || this.changes[changedKey],
            oldValue || this.original[changedKey],
            changedKey
          )
        : "";
    },
    formatCrewMembers(newValue, oldValue) {
      const changes = [];

      newValue.forEach((member) => {
        // was a member added?
        // Make sure to check the id and index to tell
        // changes when the same crew member is added multiple times
        const existingMember = oldValue.find(
          (cm) => (cm.id === member.id) & (cm.index === member.index)
        );
        if (!existingMember) {
          changes.push(`Added ${member.full_name}`);
        }

        // was a member's times changed?
        // which times were changed?
        const diff = getObjDiff(existingMember, member);
        Object.keys(diff)
          .filter((key) => supportedKeys.includes(key)) // only generate change text for keys we care about
          .forEach((changedKey) => {
            const changeText = this.getChangeText(
              changedKey,
              member[changedKey],
              existingMember[changedKey]
            );
            changes.push(
              changeText.replace("Change", `Change ${member.full_name}'s`)
            );
          });
      });

      // was a member removed?
      const removed = oldValue.filter(
        (member) =>
          !newValue.length ||
          !newValue.some(
            (cm) => cm.index === member.index && cm.id === member.id
          )
      );
      removed.forEach((member) => changes.push(`Removed ${member.full_name}`));

      return changes;
    },
    formatServiceCategory(newValue, oldValue) {
      return `Change Service Category from ${this.getServiceCategoryName(
        oldValue
      )} to ${this.getServiceCategoryName(newValue)}`;
    },
    formatTechnician(newValue, oldValue) {
      return `Change Technician from ${this.getTechnicianName(
        oldValue
      )} to ${this.getTechnicianName(newValue)}${newValue ? " (Pinned)" : ""}`;
    },
    formatDurationChange(newValue, oldValue) {
      return `Change Duration from ${this.formatDuration(
        oldValue,
        true
      )} to ${this.formatDuration(newValue, true)}`;
    },
    formatRemainingDurationChange(newValue, oldValue) {
      return `Change Estimated Remaining from ${this.remainingDurationLabel(
        oldValue
      )} to ${this.remainingDurationLabel(newValue)}`;
    },
    formatEstimatedEndTime(newValue, oldValue) {
      return `Change Estimated End Time from ${oldValue} to ${newValue}`;
    },
    formatScheduledTime(newValue, oldValue) {
      // Don't show change message until a new time has been selected
      if (!newValue) {
        return "";
      }
      return `Change Scheduled for from ${this.scheduledDateTime(
        oldValue,
        this.original.arrival_window_start,
        this.original.arrival_window_end
      )} to ${this.scheduledDateTime(
        newValue,
        this.changes.arrival_window_start,
        this.changes.arrival_window_end
      )}`;
    },
    formatStatus(newValue, oldValue) {
      return `Change Status from ${APPOINTMENT_STATUS_MAP[oldValue]} to ${APPOINTMENT_STATUS_MAP[newValue]}`;
    },
    generateDefaultMessage(newValue, oldValue, name) {
      return `Change ${name} from ${oldValue} to ${newValue}`;
    },
    formatTimeChanges(newValue, oldValue, name) {
      if (newValue === "Lost") {
        return `Clear the ${this.fieldNameMap[name]}`;
      }
      const newTime = newValue ? formatTime(newValue) : "--";
      const prevTime = oldValue ? formatTime(oldValue) : "--";
      return `Change ${this.fieldNameMap[name]} from ${prevTime} to ${newTime}`;
    },
    formatSkipNotification(newValue) {
      if (newValue) {
        return "Customer will not be notified of this scheduling change.";
      }
    },
    formatChangeAssignee(newValue, oldValue) {
      const from = oldValue || "unassigned";
      const to = newValue || "unassigned";
      return `Change assignee from ${from} to ${to}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.summary {
  background: var(--light-gray);
  border: 1px solid var(--gray);
  border-radius: 5px;
  padding: 10px;
}
</style>
